import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../context/AuthContext'
import { getDownloadUrl } from '../../r2Client'
import {
  Film, Image, FileText, Link as LinkIcon,
  CheckCircle2, Clock, AlertCircle, Archive,
  MessageSquare, ChevronRight, Send, X, RotateCcw,
} from 'lucide-react'

/* ─── helpers ─── */
const statusConfig = {
  in_revisione:       { label: 'In revisione', color: 'bg-amber-500/10 text-amber-400',    icon: Clock },
  approvato:          { label: 'Approvato',     color: 'bg-emerald-500/10 text-emerald-400', icon: CheckCircle2 },
  richiede_modifiche: { label: 'Modifiche',     color: 'bg-red-500/10 text-red-400',        icon: AlertCircle },
  archiviato:         { label: 'Archiviato',    color: 'bg-gray-700 text-gray-500',          icon: Archive },
}

const tipoIcon = (tipo) => {
  if (tipo === 'video')    return <Film size={16} className="text-blue-400" />
  if (tipo === 'immagine') return <Image size={16} className="text-violet-400" />
  if (tipo === 'pdf')      return <FileText size={16} className="text-amber-400" />
  return <LinkIcon size={16} className="text-gray-400" />
}

const fmtTime = (sec) => {
  if (!sec && sec !== 0) return null
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

/* Ordina i commenti: prima quelli con timecode (crescente), poi i generali (per data) */
const sortCommenti = (arr) => [...arr].sort((a, b) => {
  const ta = a.timecode_sec, tb = b.timecode_sec
  if (ta == null && tb == null) return new Date(a.created_at) - new Date(b.created_at)
  if (ta == null) return 1
  if (tb == null) return -1
  if (ta !== tb) return ta - tb
  return new Date(a.created_at) - new Date(b.created_at)
})

/* Rende il testo con link cliccabili (http/https) */
const renderTextWithLinks = (text) => {
  if (!text) return null
  const parts = text.split(/(https?:\/\/[^\s]+)/g)
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}
        className="underline break-all hover:opacity-80">{part}</a>
    ) : part
  )
}

const StatusBadge = ({ status }) => {
  const cfg = statusConfig[status] || statusConfig.in_revisione
  const Icon = cfg.icon
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${cfg.color}`}>
      <Icon size={11} /> {cfg.label}
    </span>
  )
}

/* ─── Watermark ─── */
const Watermark = ({ nome }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden select-none z-10">
    {Array.from({ length: 6 }).map((_, row) =>
      Array.from({ length: 4 }).map((_, col) => (
        <div
          key={`${row}-${col}`}
          className="absolute text-white/10 text-xs font-bold whitespace-nowrap"
          style={{
            top: `${row * 18 + 5}%`,
            left: `${col * 28 - 5}%`,
            transform: 'rotate(-30deg)',
            fontSize: '11px',
            letterSpacing: '0.05em',
          }}
        >
          {nome} · CONFIDENZIALE
        </div>
      ))
    )}
  </div>
)

/* ─── ViewerModal ─── */
const ViewerModal = ({ revisione, myId, nome, onClose, onStatusChange }) => {
  const [url, setUrl] = useState(null)
  const [commenti, setCommenti] = useState([])
  const [testo, setTesto] = useState('')
  const [timecode, setTimecode] = useState(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [sending, setSending] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editText, setEditText] = useState('')
  const videoRef = useRef(null)

  useEffect(() => {
    if (revisione.file_r2_key) {
      getDownloadUrl(revisione.file_r2_key).then(setUrl)
    } else if (revisione.file_url) {
      setUrl(revisione.file_url)
    }
    fetchCommenti()
    // Marca come visto
    supabase.from('revisione_destinatari')
      .update({ visto_at: new Date().toISOString() })
      .eq('revisione_id', revisione.id)
      .eq('user_id', myId)
      .is('visto_at', null)
  }, [revisione.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCommenti = async () => {
    const { data } = await supabase
      .from('revisione_commenti')
      .select('*, autore:autore_id(full_name, nickname, avatar_url)')
      .eq('revisione_id', revisione.id)
      .order('created_at', { ascending: true })
    setCommenti(sortCommenti(data || []))
  }

  const handleEditComment = async (id) => {
    if (!editText.trim()) return
    const { error } = await supabase.from('revisione_commenti')
      .update({ testo: editText.trim() }).eq('id', id)
    if (!error) {
      setCommenti(prev => prev.map(c => c.id === id ? { ...c, testo: editText.trim() } : c))
      setEditingId(null); setEditText('')
    }
  }

  const handleSend = async () => {
    if (!testo.trim() || sending) return
    setSending(true)
    const { data: newC } = await supabase.from('revisione_commenti').insert({
      revisione_id: revisione.id,
      autore_id: myId,
      testo: testo.trim(),
      timecode_sec: timecode,
    }).select('*, autore:autore_id(full_name, nickname, avatar_url)').single()

    if (newC) setCommenti(prev => sortCommenti([...prev, newC]))

    // Notifica al team
    await supabase.from('notifiche').insert({
      user_id: revisione.inviato_da,
      tipo: 'commento_revisione',
      titolo: revisione.titolo,
      body: testo.trim().substring(0, 80),
      link: '/revisioni',
    })

    setTesto('')
    setTimecode(null)
    setSending(false)
  }

  const handleStatus = async (newStatus) => {
    await supabase.from('revisioni').update({ status: newStatus }).eq('id', revisione.id)
    await supabase.from('notifiche').insert({
      user_id: revisione.inviato_da,
      tipo: newStatus === 'approvato' ? 'revisione_approvata' : 'revisione_modifiche',
      titolo: revisione.titolo,
      body: newStatus === 'approvato' ? 'Il cliente ha approvato.' : 'Il cliente richiede modifiche.',
      link: '/revisioni',
    })
    onStatusChange(revisione.id, newStatus)
  }

  const isVideo = revisione.tipo === 'video'
  const isImage = revisione.tipo === 'immagine'
  const isPdf = revisione.tipo === 'pdf'
  const isLink = revisione.tipo === 'link'

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex overflow-hidden" onClick={onClose}>
      <div className="flex w-full h-full" onClick={e => e.stopPropagation()}>

        {/* Media */}
        <div className="flex-1 flex flex-col bg-black relative">
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-900/80 backdrop-blur z-20 flex-shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              {tipoIcon(revisione.tipo)}
              <p className="text-white font-medium text-sm truncate">{revisione.titolo}</p>
              <StatusBadge status={revisione.status} />
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {revisione.status === 'in_revisione' && (
                <>
                  <button onClick={() => handleStatus('approvato')}
                    className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors">
                    <CheckCircle2 size={13} /> Approva
                  </button>
                  <button onClick={() => handleStatus('richiede_modifiche')}
                    className="flex items-center gap-1.5 bg-red-600/80 hover:bg-red-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors">
                    <RotateCcw size={13} /> Modifiche
                  </button>
                </>
              )}
              <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex items-center justify-center overflow-hidden relative">
            <Watermark nome={nome} />
            {!url && !isLink ? (
              <div className="w-10 h-10 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
            ) : isVideo ? (
              <video
                ref={videoRef}
                src={url}
                controls
                className="max-w-full max-h-full relative z-0"
                style={{ maxHeight: 'calc(100vh - 120px)' }}
                onTimeUpdate={e => setCurrentTime(e.target.currentTime)}
              />
            ) : isImage ? (
              <img src={url} alt={revisione.titolo} className="max-w-full max-h-full object-contain relative z-0" />
            ) : isPdf ? (
              <iframe src={url} title={revisione.titolo} className="w-full h-full border-0" />
            ) : (
              <div className="text-center">
                <LinkIcon size={48} className="text-gray-600 mx-auto mb-4" />
                <a href={revisione.file_url} target="_blank" rel="noopener noreferrer"
                  className="text-violet-400 hover:text-violet-300 underline text-sm break-all">
                  {revisione.file_url}
                </a>
              </div>
            )}
          </div>

          {isVideo && (
            <div className="px-4 py-2 bg-gray-900/80 flex items-center gap-3 flex-shrink-0">
              <span className="text-gray-500 text-xs">Commenta al timecode:</span>
              <button
                onClick={() => setTimecode(timecode !== null ? null : Math.floor(currentTime))}
                className={`text-xs px-2.5 py-1 rounded-lg transition-colors ${timecode !== null ? 'bg-violet-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
                {timecode !== null ? `⏱ ${fmtTime(timecode)}` : `@ ${fmtTime(Math.floor(currentTime))}`}
              </button>
            </div>
          )}
        </div>

        {/* Commenti */}
        <div className="w-80 bg-gray-900 border-l border-gray-800 flex flex-col flex-shrink-0">
          <div className="px-4 py-3 border-b border-gray-800 flex-shrink-0">
            <p className="text-white font-semibold text-sm flex items-center gap-2">
              <MessageSquare size={15} /> Commenti ({commenti.length})
            </p>
            {revisione.descrizione && (
              <p className="text-gray-500 text-xs mt-2 italic">"{revisione.descrizione}"</p>
            )}
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {commenti.length === 0 && (
              <p className="text-gray-600 text-xs text-center py-6">Nessun commento ancora.<br />Scrivi il tuo feedback qui sotto.</p>
            )}
            {commenti.map(c => {
              const autoreNome = c.autore?.nickname || c.autore?.full_name || 'Utente'
              const isMe = c.autore_id === myId
              return (
                <div key={c.id} className={`${isMe ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                  {c.timecode_sec != null && (
                    <button
                      onClick={() => { if (videoRef.current) { videoRef.current.currentTime = c.timecode_sec; videoRef.current.play() } }}
                      className="text-[10px] text-violet-400 hover:text-violet-300 transition-colors">
                      ⏱ {fmtTime(c.timecode_sec)}
                    </button>
                  )}
                  <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm ${isMe ? 'bg-violet-600 text-white rounded-br-sm' : 'bg-gray-800 text-gray-200 rounded-bl-sm'}`}>
                    {!isMe && <p className="text-xs font-medium mb-1 text-violet-300">{autoreNome}</p>}
                    {editingId === c.id ? (
                      <div className="space-y-1.5">
                        <textarea
                          value={editText}
                          onChange={e => setEditText(e.target.value)}
                          rows={2}
                          autoFocus
                          className="w-full bg-gray-900/50 border border-white/20 rounded-lg px-2 py-1 text-sm text-white focus:outline-none resize-none"
                        />
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => { setEditingId(null); setEditText('') }} className="text-[10px] opacity-70 hover:opacity-100">Annulla</button>
                          <button onClick={() => handleEditComment(c.id)} className="text-[10px] font-medium underline">Salva</button>
                        </div>
                      </div>
                    ) : (
                      <p className="leading-relaxed break-words whitespace-pre-wrap">{renderTextWithLinks(c.testo)}</p>
                    )}
                    <div className={`flex items-center gap-2 mt-1 ${isMe ? 'justify-end' : ''}`}>
                      <span className={`text-[10px] ${isMe ? 'text-violet-200' : 'text-gray-500'}`}>
                        {new Date(c.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {isMe && editingId !== c.id && (
                        <button
                          onClick={() => { setEditingId(c.id); setEditText(c.testo) }}
                          className="text-[10px] text-violet-200 hover:text-white underline">
                          modifica
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="px-4 py-3 border-t border-gray-800 flex-shrink-0">
            {timecode !== null && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-violet-400 bg-violet-600/10 px-2 py-1 rounded-lg">⏱ {fmtTime(timecode)}</span>
                <button onClick={() => setTimecode(null)} className="text-gray-600 hover:text-gray-400"><X size={12} /></button>
              </div>
            )}
            <div className="flex items-end gap-2">
              <textarea
                value={testo}
                onChange={e => setTesto(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }}
                placeholder="Il tuo feedback..."
                rows={1}
                className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors resize-none"
                style={{ height: '38px' }}
              />
              <button
                onClick={handleSend}
                disabled={!testo.trim() || sending}
                className="flex-shrink-0 w-9 h-9 bg-violet-600 hover:bg-violet-500 disabled:opacity-40 rounded-xl flex items-center justify-center transition-colors">
                <Send size={14} className="text-white" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── Main ─── */
const ClientRevisioni = () => {
  const { profile } = useAuth()
  const [revisioni, setRevisioni] = useState([])
  const [loading, setLoading] = useState(true)
  const [viewer, setViewer] = useState(null)
  const myId = profile?.id
  const nome = profile?.nickname || profile?.full_name || 'Cliente'

  const fetchRevisioni = useCallback(async () => {
    if (!myId) return
    const { data } = await supabase
      .from('revisioni')
      .select('*, progetto:progetto_id(titolo, colore)')
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
    setRevisioni(data || [])
    setLoading(false)
  }, [myId])

  useEffect(() => { fetchRevisioni() }, [fetchRevisioni])

  const handleStatusChange = (id, newStatus) => {
    setRevisioni(prev => prev.map(r => r.id === id ? { ...r, status: newStatus } : r))
    if (viewer?.id === id) setViewer(prev => ({ ...prev, status: newStatus }))
  }

  return (
    <div className="max-w-3xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Revisioni</h1>
        <p className="text-gray-500 text-sm mt-1">Guarda i materiali, lascia il tuo feedback e approva.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : revisioni.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-12 text-center">
          <Film size={36} className="text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Nessuna revisione ancora.</p>
          <p className="text-gray-600 text-xs mt-1">Il team di Plutonic Studio ti invierà qui i materiali da revisionare.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {revisioni.map(r => (
            <div
              key={r.id}
              onClick={() => setViewer(r)}
              className="bg-gray-900 border border-gray-800 rounded-2xl px-5 py-4 flex items-center gap-4 hover:border-gray-700 transition-colors cursor-pointer group"
            >
              <div className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center flex-shrink-0">
                {tipoIcon(r.tipo)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{r.titolo}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                  {r.progetto && (
                    <span className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: r.progetto.colore || '#6d28d9' }} />
                      {r.progetto.titolo}
                    </span>
                  )}
                  <span>·</span>
                  <span>{new Date(r.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                </div>
              </div>
              <StatusBadge status={r.status} />
              <ChevronRight size={16} className="text-gray-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          ))}
        </div>
      )}

      {viewer && (
        <ViewerModal
          revisione={viewer}
          myId={myId}
          nome={nome}
          onClose={() => setViewer(null)}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  )
}

export default ClientRevisioni
